// src/pages/Dashboard/components/PracticeReps.jsx
// STATUS: active | UPDATED: 2026-08-30
// LOG:
//   2026-08-30  New. The person doing the reps could not see they were doing them.
//
// A manager can see who is practising. An owner can see how much the floor is
// practising. The salesperson running the drills could see none of it, which is
// backwards: the person doing the work was the only one it was invisible to.
//
// WHY THIS IS NOT A STREAK
//
// The streak badge next to it already rewards showing up. Rewarding showing up
// twice would be padding. This counts reps, which is a different and better
// thing, because a rep is voluntary and a login is not.
//
// EARNED, OR SILENT
//
// Same rule as the manager's recognition card. With no drills at all this is an
// invitation, not a scold, and it never says "you should be practising more".
// With drills it states the count and names the step, because "you have run
// Step 6 four times" is worth reading and "keep it up" is not.
//
// It also never compares them to anybody. A salesperson seeing that they are
// behind a colleague on training is a decision Jazz should make about his own
// floors, not one this component makes for him.

import { useEffect, useState } from 'react'
import { HStack, VStack, Text, Flex, Icon, Button } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Dumbbell, ArrowRight } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { STEPS } from '../../../content/steps'

const DAY = 24 * 60 * 60 * 1000

export default function PracticeReps({ userId, currentStep }) {
  const [state, setState] = useState({ loading: true, count: 0, topStep: null })

  useEffect(() => {
    if (!userId) return undefined
    let live = true
    const since = new Date(Date.now() - 7 * DAY).toISOString()

    supabase
      .from('activity_events')
      .select('metadata')
      .eq('user_id', userId)
      .eq('kind', 'practice_drill')
      .gte('created_at', since)
      .then(({ data, error }) => {
        if (!live) return
        if (error) {
          // A read that failed is not a zero. Render nothing rather than tell
          // somebody they have not practised when we simply could not look.
          setState({ loading: false, count: null, topStep: null })
          return
        }
        const rows = data || []
        const byStep = {}
        for (const r of rows) {
          const n = r.metadata && Number(r.metadata.step)
          if (Number.isInteger(n)) byStep[n] = (byStep[n] || 0) + 1
        }
        const top = Object.entries(byStep).sort((a, b) => b[1] - a[1])[0]
        setState({
          loading: false,
          count: rows.length,
          topStep: top ? { number: Number(top[0]), reps: top[1] } : null,
        })
      })

    return () => {
      live = false
    }
  }, [userId])

  if (state.loading || state.count === null) return null

  const { count, topStep } = state
  const step = topStep && STEPS.find((s) => s.number === topStep.number)
  const drillTarget = currentStep?.number || 1

  return (
    <HStack
      spacing={4}
      px={5}
      py={4}
      bg={count > 0 ? 'ink' : 'card'}
      color={count > 0 ? 'onInk' : undefined}
      borderRadius="card"
      border="1px solid"
      borderColor={count > 0 ? 'ink' : 'line'}
      align={{ base: 'flex-start', sm: 'center' }}
      flexDir={{ base: 'column', sm: 'row' }}
    >
      <Flex
        w={9}
        h={9}
        borderRadius="full"
        bg={count > 0 ? 'onInkFill' : 'accent.soft'}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={Dumbbell} boxSize={4} color={count > 0 ? 'onInk' : 'accent.500'} />
      </Flex>

      <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
        {count > 0 ? (
          <>
            <Text fontSize="lg" fontWeight={700} lineHeight={1.15}>
              {count} {count === 1 ? 'drill' : 'drills'} this week
            </Text>
            <Text fontSize="xs" color="onInkMuted">
              {step && topStep.reps > 1
                ? `Most on Step ${topStep.number}, ${step.title}. Nobody made you do that.`
                : 'Nobody made you do that.'}
            </Text>
          </>
        ) : (
          <>
            <Text fontSize="lg" fontWeight={700} color="ink" lineHeight={1.15}>
              Run one before your next customer
            </Text>
            <Text fontSize="xs" color="inkMuted">
              The coach plays the customer, then tells you what to keep and what to change.
            </Text>
          </>
        )}
      </VStack>

      <Button
        as={RouterLink}
        to={`/coach/?practice=${drillTarget}`}
        size="sm"
        rightIcon={<ArrowRight size={14} />}
        bg={count > 0 ? 'onInk' : 'ink'}
        color={count > 0 ? 'ink' : 'onInk'}
        _hover={{ opacity: 0.88 }}
        flexShrink={0}
        w={{ base: 'full', sm: 'auto' }}
      >
        {count > 0 ? 'Another' : 'Start a drill'}
      </Button>
    </HStack>
  )
}
